import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { ExternalLink, Star } from 'lucide-react';
import { useLiveTickers, useLiveTrade } from '../../lib/crypto/useBinanceStream';

const fmtCap = (v) => {
  if (v == null) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const pct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const pctClass = (v) => v == null ? 'text-[var(--txt-muted)]' : v >= 0 ? 'text-buy' : 'text-sell';

/**
 * CoinInfo — right-sidebar panel. Live-updates via WebSocket:
 *   • `useBinanceTrade` for the flashing last-print price (~10-50 Hz
 *     on liquid pairs — same feel as TradingView).
 *   • `useBinanceTickers` for 24h stats (~1 Hz).
 * The static coin metadata (mcap, supply, ATH/ATL, description) is
 * fetched once from CoinGecko per symbol change.
 */
export default function CoinInfo({ symbol, base, quote, isFav, onToggleFav }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const mr = await api.get(`/crypto/meta/${base}`);
        if (!cancelled) setMeta(mr.data);
      } catch { if (!cancelled) setMeta(null); }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [base]);

  const tickers = useLiveTickers(useMemo(() => symbol ? [symbol] : [], [symbol]));
  const t = tickers[symbol?.toUpperCase()] || null;
  const trade = useLiveTrade(symbol);

  // Flash animation on price change.
  const lastPriceRef = useRef(null);
  const [flash, setFlash] = useState(null);   // 'up' | 'down' | null
  useEffect(() => {
    const p = trade?.price ?? t?.last;
    if (p == null) return;
    if (lastPriceRef.current != null && p !== lastPriceRef.current) {
      setFlash(p > lastPriceRef.current ? 'up' : 'down');
      const to = setTimeout(() => setFlash(null), 240);
      lastPriceRef.current = p;
      return () => clearTimeout(to);
    }
    lastPriceRef.current = p;
  }, [trade?.price, t?.last]);

  const displayPrice = trade?.price ?? t?.last;
  const chg = t?.change_pct;

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)] p-3 flex flex-col gap-2" data-testid="crypto-coin-info">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys">// coin <span className="ml-1 text-[8px] text-buy">● LIVE</span></div>
          <div className="text-[20px] font-mono text-[var(--txt-primary)] leading-tight">{base}</div>
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">
            {symbol} · {meta?.name || '—'}
            {meta?.market_cap_rank && ` · #${meta.market_cap_rank}`}
          </div>
        </div>
        <button
          onClick={onToggleFav}
          className={isFav ? 'text-hold' : 'text-[var(--txt-muted)] hover:text-hold'}
          aria-label="favorite"
          data-testid="crypto-info-favorite"
        >
          <Star size={16} fill={isFav ? 'currentColor' : 'none'} />
        </button>
      </div>

      <div>
        <div
          data-testid="crypto-info-price"
          className={`text-[28px] font-mono tabular-nums leading-tight transition-colors duration-200 ${flash === 'up' ? 'text-buy' : flash === 'down' ? 'text-sell' : 'text-[var(--txt-primary)]'}`}
        >
          {displayPrice != null ? `$${displayPrice.toLocaleString(undefined, { maximumFractionDigits: displayPrice < 1 ? 6 : 2 })}` : '—'}
        </div>
        <div className={`text-[11px] font-mono tabular-nums ${pctClass(chg)}`}>
          {chg != null ? `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}% · 24h` : '—'}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono border-t border-[var(--border-default)] pt-2">
        <div><span className="text-[var(--txt-muted)]">24h vol</span><br /><span className="tabular-nums">{t?.quote_vol != null ? fmtCap(t.quote_vol) : '—'}</span></div>
        <div><span className="text-[var(--txt-muted)]">24h high</span><br /><span className="tabular-nums">{t?.high != null ? `$${t.high.toLocaleString()}` : '—'}</span></div>
        <div><span className="text-[var(--txt-muted)]">24h low</span><br /><span className="tabular-nums">{t?.low != null ? `$${t.low.toLocaleString()}` : '—'}</span></div>
        <div><span className="text-[var(--txt-muted)]">Trades</span><br /><span className="tabular-nums">{t?.trade_count?.toLocaleString() || '—'}</span></div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[10px] font-mono border-t border-[var(--border-default)] pt-2">
        <div><span className="text-[var(--txt-muted)]">7d</span><br /><span className={`tabular-nums ${pctClass(meta?.price_change_7d)}`}>{pct(meta?.price_change_7d)}</span></div>
        <div><span className="text-[var(--txt-muted)]">30d</span><br /><span className={`tabular-nums ${pctClass(meta?.price_change_30d)}`}>{pct(meta?.price_change_30d)}</span></div>
        <div><span className="text-[var(--txt-muted)]">Rank</span><br /><span className="tabular-nums text-[var(--txt-primary)]">#{meta?.market_cap_rank || '—'}</span></div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono border-t border-[var(--border-default)] pt-2">
        <div><span className="text-[var(--txt-muted)]">Market cap</span><br /><span className="tabular-nums">{fmtCap(meta?.market_cap)}</span></div>
        <div><span className="text-[var(--txt-muted)]">FDV</span><br /><span className="tabular-nums">{fmtCap(meta?.fdv)}</span></div>
        <div><span className="text-[var(--txt-muted)]">Circulating</span><br /><span className="tabular-nums">{meta?.circulating_supply ? fmtCap(meta.circulating_supply).replace('$', '') : '—'}</span></div>
        <div><span className="text-[var(--txt-muted)]">Max supply</span><br /><span className="tabular-nums">{meta?.max_supply ? fmtCap(meta.max_supply).replace('$', '') : '∞'}</span></div>
        <div><span className="text-[var(--txt-muted)]">ATH</span><br /><span className="tabular-nums text-buy">{meta?.ath != null ? `$${meta.ath.toLocaleString()}` : '—'}</span></div>
        <div><span className="text-[var(--txt-muted)]">ATL</span><br /><span className="tabular-nums text-sell">{meta?.atl != null ? `$${meta.atl.toLocaleString()}` : '—'}</span></div>
      </div>

      {meta?.description && (
        <div className="text-[10px] text-[var(--txt-secondary)] border-t border-[var(--border-default)] pt-2 leading-relaxed line-clamp-4">
          {meta.description}
        </div>
      )}
      {meta?.homepage && (
        <a href={meta.homepage} target="_blank" rel="noreferrer"
           className="text-[10px] text-cyan-sys hover:underline inline-flex items-center gap-1">
          {meta.homepage.replace(/^https?:\/\//, '').replace(/\/$/, '')} <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
}
